const CHART_CATEGORIES = [
    { label: 'Papel',       color: '#378ADD', keywords: ['papel', 'papelao', 'cartao', 'papelão', 'cartão'] },
    { label: 'Metal',       color: '#EF9F27', keywords: ['metal', 'alumin', 'aco', 'aço', 'ferro', 'cobre', 'lata'] },
    { label: 'Plástico',    color: '#E24B4A', keywords: ['plastic', 'plastico', 'plástico', 'pet', 'pead'] },
    { label: 'Vidro',       color: '#639922', keywords: ['vidro'] },
    { label: 'Eletrônico',  color: '#7F77DD', keywords: ['eletr', 'pilha', 'bateria', 'lampada', 'lâmpada'] },
    { label: 'Óleo',        color: '#D85A30', keywords: ['oleo', 'óleo', 'oil', 'gordura'] },
    { label: 'Outros',      color: '#888780', keywords: [] },
];

function normalizeCategory(material) {
    const m = material.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
    for (const cat of CHART_CATEGORIES.slice(0, -1)) {
        if (cat.keywords.some(k => m.includes(k))) return cat.label;
    }
    return 'Outros';
}

const _defaultState = () => ({
    profile: null,
    address: null,
    total_weight: null,
    weighings: null,
    weighings_summary: null,
    team: null,
    invoices: null,
    pending_summary: null,
    collection_points: null,
    materials: null,
    pending_review_orders: null,
});

const state = _defaultState();

const mutations = {
    SET_CLIENT_PROFILE(state, profile) {
        state.profile = profile;
    },
    SET_CLIENT_ADDRESS(state, address) {
        state.address = address;
    },
    SET_CLIENT_TOTAL_WEIGHT(state, weight) {
        state.total_weight = weight;
    },
    SET_CLIENT_WEIGHINGS(state, weighings) {
        state.weighings = weighings;
    },
    SET_CLIENT_WEIGHINGS_SUMMARY(state, summary) {
        state.weighings_summary = summary;
    },
    SET_CLIENT_TEAM(state, team) {
        state.team = team;
    },
    SET_CLIENT_INVOICES(state, invoices) {
        state.invoices = invoices;
    },
    SET_CLIENT_PENDING_SUMMARY(state, summary) {
        state.pending_summary = summary;
    },
    SET_CLIENT_COLLECTION_POINTS(state, points) {
        state.collection_points = points;
    },
    SET_CLIENT_MATERIALS(state, materials) {
        state.materials = materials;
    },
    SET_CLIENT_PENDING_REVIEW_ORDERS(state, orders) {
        state.pending_review_orders = orders;
    },
    RESET_CLIENT_ACCOUNT_STATE(state) {
        Object.assign(state, _defaultState());
    },
};

const getters = {
    get_profile(state) { return state.profile; },
    get_address(state) { return state.address; },
    get_full_name(state) {
        if (!state.profile) return null;
        return `${state.profile.first_name} ${state.profile.last_name}`;
    },
    get_total_weight(state) { return state.total_weight; },
    get_weighings(state) { return state.weighings; },
    get_weighings_summary(state) { return state.weighings_summary; },
    get_weighings_count(state) {
        return state.weighings ? state.weighings.length : null;
    },
    get_team(state) { return state.team; },
    get_invoices(state) { return state.invoices; },
    get_pending_summary(state) { return state.pending_summary; },
    get_collection_points(state) { return state.collection_points; },
    get_materials(state) { return state.materials; },
    get_pending_review_orders(state) { return state.pending_review_orders; },
    get_chart_data(state) {
        if (!state.weighings || !state.weighings.length) return null;

        const totals = Object.fromEntries(CHART_CATEGORIES.map(c => [c.label, 0]));

        for (const weighing of state.weighings) {
            if (weighing.items?.length) {
                for (const item of weighing.items) {
                    const cat = normalizeCategory(item.material || '');
                    totals[cat] += item.weights;
                }
            } else if (weighing.material?.name) {
                const cat = normalizeCategory(weighing.material.name);
                totals[cat] += parseFloat(weighing.total_weight || 0);
            } else {
                totals['Outros'] += parseFloat(weighing.total_weight || 0);
            }
        }

        return CHART_CATEGORIES.map(c => ({
            label: c.label,
            value: Math.round(totals[c.label] * 100) / 100,
            color: c.color,
        }));
    },
};

const actions = {
    async fetchProfile({ commit, rootGetters }) {
        const { ct_clients } = await import('@/controllers/clients');
        const { access_token } = rootGetters['clients_users/get_client_data'];

        const response = await ct_clients.getProfile(access_token);
        if (!response.status) throw response.error;

        commit('SET_CLIENT_PROFILE', response.data);
        if (response.data?.address) {
            commit('SET_CLIENT_ADDRESS', response.data.address);
        }

        return response.data;
    },

    async updateProfile({ dispatch, rootGetters }, data) {
        const { ct_clients } = await import('@/controllers/clients');
        const { access_token } = rootGetters['clients_users/get_client_data'];

        const response = await ct_clients.updateProfile(access_token, data);
        if (!response.status) throw response.error;

        await dispatch('fetchProfile');
        return response;
    },

    async setAddress({ commit, rootGetters }, data) {
        const { ct_clients } = await import('@/controllers/clients');
        const { access_token } = rootGetters['clients_users/get_client_data'];

        const response = await ct_clients.setAddress(access_token, data);
        if (!response.status) throw response.error;

        commit('SET_CLIENT_ADDRESS', data);
        return response;
    },

    async fetchWeighings({ commit, rootGetters }) {
        const { ct_clients } = await import('@/controllers/clients');
        const { access_token } = rootGetters['clients_users/get_client_data'];

        const response = await ct_clients.getWeighings(access_token);
        if (!response.status) throw response.error;

        const data = response.data;
        const summary = response.summary;
        commit('SET_CLIENT_WEIGHINGS', data);
        commit('SET_CLIENT_WEIGHINGS_SUMMARY', summary ?? null);
        commit('SET_CLIENT_TOTAL_WEIGHT', summary?.total_weight ?? data.reduce((acc, w) => {
            if (w.items?.length) return acc + w.items.reduce((s, i) => s + i.weights, 0);
            return acc + parseFloat(w.total_weight || 0);
        }, 0));

        return data;
    },

    async fetchTeam({ commit, rootGetters }) {
        const { ct_clients } = await import('@/controllers/clients');
        const { access_token } = rootGetters['clients_users/get_client_data'];

        const response = await ct_clients.getTeam(access_token);
        if (!response.status) throw response.error;

        commit('SET_CLIENT_TEAM', response.data);
        return response.data;
    },

    async fetchInvoices({ commit, rootGetters }) {
        const { ct_clients } = await import('@/controllers/clients');
        const { access_token } = rootGetters['clients_users/get_client_data'];
        const response = await ct_clients.getInvoices(access_token);
        if (!response.status) throw response.error;
        commit('SET_CLIENT_INVOICES', response.data);
        return response.data;
    },

    async fetchPendingSummary({ commit, rootGetters }) {
        const { ct_clients } = await import('@/controllers/clients');
        const { access_token } = rootGetters['clients_users/get_client_data'];
        const response = await ct_clients.getPendingInvoices(access_token);
        if (!response.status) throw response.error;
        commit('SET_CLIENT_PENDING_SUMMARY', response.data);
        return response.data;
    },

    async payInvoice({ dispatch, rootGetters }, id) {
        const { ct_clients } = await import('@/controllers/clients');
        const { access_token } = rootGetters['clients_users/get_client_data'];
        const response = await ct_clients.payInvoice(access_token, id);
        if (!response.status) throw response.error;
        await dispatch('fetchInvoices');
        await dispatch('fetchPendingSummary');
        return response;
    },

    async fetchCollectionPoints({ commit, rootGetters }) {
        const { ct_clients } = await import('@/controllers/clients');
        const { access_token } = rootGetters['clients_users/get_client_data'];

        const response = await ct_clients.getCollectionPoints(access_token);
        if (!response.status) throw response.error;

        commit('SET_CLIENT_COLLECTION_POINTS', response.data);
        return response.data;
    },

    async createCollectionPoint({ dispatch, rootGetters }, data) {
        const { ct_clients } = await import('@/controllers/clients');
        const { access_token } = rootGetters['clients_users/get_client_data'];

        const response = await ct_clients.createCollectionPoint(access_token, data);
        if (!response.status) throw response.error;

        await dispatch('fetchCollectionPoints');
        return response;
    },

    async deleteCollectionPoint({ dispatch, rootGetters }, id) {
        const { ct_clients } = await import('@/controllers/clients');
        const { access_token } = rootGetters['clients_users/get_client_data'];

        const response = await ct_clients.deleteCollectionPoint(access_token, id);
        if (!response.status) throw response.error;

        await dispatch('fetchCollectionPoints');
        return response;
    },

    async createTeamMember({ dispatch, rootGetters }, data) {
        const { ct_clients } = await import('@/controllers/clients');
        const { access_token } = rootGetters['clients_users/get_client_data'];

        const response = await ct_clients.createTeamMember(access_token, data);
        if (!response.status) throw response.error;

        await dispatch('fetchTeam');
        return response;
    },

    async submitWeighing({ dispatch, rootGetters }, payload) {
        const { ct_clients } = await import('@/controllers/clients');
        const { access_token } = rootGetters['clients_users/get_client_data'];

        const data = {};
        ['email', 'cpf', 'phone'].forEach(key => {
            if (payload[key]) data[key] = payload[key];
        });
        data.weights = payload.weights;
        if (payload.material_id) data.material_id = payload.material_id;
        if (payload.obs) data.obs = payload.obs;

        const response = await ct_clients.submitWeighing(access_token, data);
        if (!response.status) throw response.error;

        await dispatch('fetchWeighings');
        return response;
    },

    async fetchMaterials({ commit, rootGetters }) {
        const { ct_clients } = await import('@/controllers/clients');
        const { access_token } = rootGetters['clients_users/get_client_data'];
        const response = await ct_clients.getMaterials(access_token);
        if (!response.status) throw response.error;
        commit('SET_CLIENT_MATERIALS', response.data);
        return response.data;
    },

    async createMaterial({ dispatch, rootGetters }, data) {
        const { ct_clients } = await import('@/controllers/clients');
        const { access_token } = rootGetters['clients_users/get_client_data'];
        const response = await ct_clients.createMaterial(access_token, data);
        if (!response.status) throw response.error;
        await dispatch('fetchMaterials');
        return response;
    },

    async updateMaterial({ dispatch, rootGetters }, { id, data }) {
        const { ct_clients } = await import('@/controllers/clients');
        const { access_token } = rootGetters['clients_users/get_client_data'];
        const response = await ct_clients.updateMaterial(access_token, id, data);
        if (!response.status) throw response.error;
        await dispatch('fetchMaterials');
        return response;
    },

    async deleteMaterial({ dispatch, rootGetters }, id) {
        const { ct_clients } = await import('@/controllers/clients');
        const { access_token } = rootGetters['clients_users/get_client_data'];
        const response = await ct_clients.deleteMaterial(access_token, id);
        if (!response.status) throw response.error;
        await dispatch('fetchMaterials');
        return response;
    },

    async fetchPendingReviewOrders({ commit, rootGetters }) {
        const { ct_clients } = await import('@/controllers/clients');
        const { access_token } = rootGetters['clients_users/get_client_data'];
        const response = await ct_clients.getPendingReviewOrders(access_token);
        if (!response.status) throw response.error;
        commit('SET_CLIENT_PENDING_REVIEW_ORDERS', response.data);
        return response.data;
    },

    async resolveOrder({ dispatch, rootGetters }, { orderCode, materialId }) {
        const { ct_clients } = await import('@/controllers/clients');
        const { access_token } = rootGetters['clients_users/get_client_data'];
        const response = await ct_clients.resolveOrder(access_token, orderCode, materialId);
        if (!response.status) throw response.error;
        await Promise.all([
            dispatch('fetchPendingReviewOrders'),
            dispatch('fetchWeighings'),
        ]);
        return response;
    },
};

const clients_account = {
    namespaced: true,
    state,
    mutations,
    getters,
    actions,
};

export default clients_account;

